import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuthState } from '../state/auth-state';

type ProtectedRouteProps = {
  children?: React.ReactNode;
  redirectTo: string;
  loadingFallback?: React.ReactNode;
};

const defaultLoadingFallback = <div />;

export const ProtectedRoute = ({
  children,
  redirectTo,
  loadingFallback = defaultLoadingFallback,
}: ProtectedRouteProps) => {
  const { status, setIntendedRoute } = useAuthState();
  const location = useLocation();
  const previousStatusRef = React.useRef<typeof status>(status);

  React.useEffect(() => {
    // Already authenticated – nothing to persist.
    if (status === 'authenticated') {
      previousStatusRef.current = status;
      return;
    }

    // Only remember the path when the user tried to access this page while unauthenticated
    // (e.g. direct navigation or refresh). Do not overwrite when they just logged out
    // (status transition from authenticated -> unauthenticated), so they land on default after next login.
    const wasAuthenticated = previousStatusRef.current === 'authenticated';
    previousStatusRef.current = status;
    if (wasAuthenticated) {
      return;
    }

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (!currentPath || currentPath === redirectTo) {
      return;
    }

    setIntendedRoute(currentPath);
  }, [status, setIntendedRoute, location, redirectTo]);

  // Show placeholder while we determine the user's authentication state.
  if (status === 'loading' || status === 'idle') {
    return <>{loadingFallback}</>;
  }

  // Authenticated users are free to access the protected content.
  if (status === 'authenticated') {
    return <>{children ?? <Outlet />}</>;
  }

  // Otherwise push them to the sign-in route and record where they came from.
  return <Navigate replace={true} state={{ from: location }} to={redirectTo} />;
};
